import {
    Directive,
    ElementRef,
    HostListener,
    input,
    OnDestroy,
    TemplateRef,
    ViewContainerRef,
} from "@angular/core";
import {
    Overlay,
    OverlayRef,
    OverlayPositionBuilder,
    ConnectedPosition,
} from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";
import { Component, ChangeDetectionStrategy } from "@angular/core";

type TooltipPosition = "top" | "bottom" | "left" | "right";

// Tooltip 컨텐츠를 표시할 컴포넌트
@Component({
    selector: "app-tooltip-content",
    template: `<div class="tooltip-content">{{ text }}</div>`,
    styles: [
        `
            .tooltip-content {
                background: rgba(0, 0, 0, 0.75);
                color: #fff;
                padding: 5px 10px;
                border-radius: 4px;
                font-size: 14px;
                white-space: pre-line;
                animation: tooltipFadeIn 0.1s ease-out;
            }

            @keyframes tooltipFadeIn {
                from {
                    opacity: 0;
                    transform: scale(0.8);
                }
                to {
                    opacity: 1;
                    transform: scale(1);
                }
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TooltipContentComponent {
    text = "";
}

@Directive({
    selector: "[appTooltip]",
})
export class TooltipDirective implements OnDestroy {
    text = input<string>("", { alias: "appTooltip" });
    tooltipPosition = input<TooltipPosition>("bottom");

    private overlayRef: OverlayRef | null = null;

    constructor(
        private overlay: Overlay,
        private overlayPositionBuilder: OverlayPositionBuilder,
        private elementRef: ElementRef,
        private viewContainerRef: ViewContainerRef,
    ) {}

    @HostListener("mouseenter")
    show() {
        // 모바일에서는 tooltip 비활성화
        if (window.innerWidth < 1024) {
            return;
        }

        if (!this.text()) {
            return;
        }

        // 이미 열려있으면 무시
        if (this.overlayRef?.hasAttached()) {
            return;
        }

        const positionStrategy = this.overlayPositionBuilder
            .flexibleConnectedTo(this.elementRef)
            .withPositions(this.getPositions())
            .withPush(true); // 화면 밖으로 나가면 자동으로 밀어냄

        this.overlayRef = this.overlay.create({
            positionStrategy,
            scrollStrategy: this.overlay.scrollStrategies.close(), // 스크롤 시 닫기
            panelClass: "tooltip-overlay-panel",
        });

        const tooltipPortal = new ComponentPortal(
            TooltipContentComponent,
            this.viewContainerRef,
        );
        const tooltipRef = this.overlayRef.attach(tooltipPortal);
        tooltipRef.instance.text = this.text();
    }

    @HostListener("mouseleave")
    hide() {
        this.overlayRef?.detach();
    }

    ngOnDestroy() {
        this.overlayRef?.dispose();
    }

    private getPositions(): ConnectedPosition[] {
        const margin = 5;

        const positions: Record<TooltipPosition, ConnectedPosition[]> = {
            top: [
                {
                    originX: "center",
                    originY: "top",
                    overlayX: "center",
                    overlayY: "bottom",
                    offsetY: -margin,
                },
                // fallback: bottom
                {
                    originX: "center",
                    originY: "bottom",
                    overlayX: "center",
                    overlayY: "top",
                    offsetY: margin,
                },
            ],
            bottom: [
                {
                    originX: "center",
                    originY: "bottom",
                    overlayX: "center",
                    overlayY: "top",
                    offsetY: margin,
                },
                // fallback: top
                {
                    originX: "center",
                    originY: "top",
                    overlayX: "center",
                    overlayY: "bottom",
                    offsetY: -margin,
                },
            ],
            left: [
                {
                    originX: "start",
                    originY: "center",
                    overlayX: "end",
                    overlayY: "center",
                    offsetX: -margin,
                },
                // fallback: right
                {
                    originX: "end",
                    originY: "center",
                    overlayX: "start",
                    overlayY: "center",
                    offsetX: margin,
                },
            ],
            right: [
                {
                    originX: "end",
                    originY: "center",
                    overlayX: "start",
                    overlayY: "center",
                    offsetX: margin,
                },
                // fallback: left
                {
                    originX: "start",
                    originY: "center",
                    overlayX: "end",
                    overlayY: "center",
                    offsetX: -margin,
                },
            ],
        };

        return positions[this.tooltipPosition()];
    }
}
